/* globals logLevels */
import Logger from '../utils/logger/logger'
import { actions, assign, createMachine, send, sendParent } from 'xstate'
import broadcast from '../utils/subscription_utils/broadcast'
import {
  evaluateBranching,
  getCurrentQuestionGroupIndex,
  getNextQuestionGroup,
  prepareActors,
  updateMachineContext,
  updateQuestionGroupMeta,
  awaitTransmissionsDone, prepareResponses
} from '../utils/machine_utils/QuestionMachine.utils'
import { storeForRetargeting } from '../utils/retargetStore'
import { questionDelays } from '../events/delayMap'
import { broadcastEvents, funnelEvents, inputEvents, questionEvents } from '../events/eventMap'

export const questionMachineDeclaration = {
  id: 'question',
  initial: 'prep',
  predictableActionArguments: true,
  context: {
    question_group: null,
    autofocus_enabled: false,
    disableBackNavigation: false,
    responses: []
    // values transferred into the context from the FunnelMachine invocation are
    // userId, sessionUUID, responses, question_groups, eventSubscriptions
  },
  states: {
    prep: {
      entry: ['setInitialQuestionGroup', 'readyToParent'],
      always: {
        target: 'editing'
      },
      exit: 'createActors'
    },
    intermission: {
      tags: ['intermission'], // tags are used to identify state value due to child state usage
      after: {
        [questionDelays.intermissionDelay]: { target: 'editing' }
      },
      exit: 'completeIntermission'
    },
    editing: {
      tags: ['editing'], // tags are used to identify state value due to child state usage
      initial: 'active',
      states: {
        active: {
          initial: 'manualNav',
          always: [{
            target: 'delayedBillboard',
            cond: 'isTimedBillboard'
          },
          {
            target: 'loadingPanel',
            cond: 'isLoadingPanel'
          }],
          states: {
            hist: {
              type: 'history'
            },
            manualNav: {},
            autoNav: {
              invoke: {
                id: 'autoNav',
                src: ctx => callback => {
                  const id = setInterval(() => {
                    if (ctx.question_group.group_key === ctx.autoNavTargetGroup) {
                      callback(questionEvents.exitAutoNav)
                    } else {
                      callback(questionEvents.next)
                    }
                  }, 0)

                  return () => clearInterval(id)
                },
                onDone: {
                  target: 'manualNav'
                }
              }
            }
          },
          on: {
            [questionEvents.exitAutoNav]: {
              target: 'active.manualNav'
            }
          }
        },
        delayedBillboard: {
          after: {
            [questionDelays.billboardDelay]: {
              actions: ['logEvent', 'requestNextQuestionGroup']
            }
          }
        },
        loadingPanel: {
          after: {
            [questionDelays.loadingPanelDelay]: {
              actions: ['logEvent', 'requestNextQuestionGroup']
            }
          }
        },
        validating: {
          always: {
            actions: ['startValidation'], // trigger VALIDATE event for all actors within the current question group
            target: 'active.hist'
          }
        }
      },
      always: {
        target: 'intermission',
        cond: 'canEnterIntermission'
      },
      on: {
        [questionEvents.updateAnswer]: [
          {
            actions: [
              'logEvent',
              'storeForRetargeting',
              'updateResponses',
              'evaluateBranching',
              'requestNextQuestionGroup'
            ],
            cond: 'isSingleClick'
          },
          {
            actions: ['logEvent', 'storeForRetargeting', 'updateResponses', 'evaluateBranching']
          }
        ],
        [questionEvents.updatePopupURL]: {
          actions: ['assignPopupURL']
        },
        [questionEvents.updateActorAnswers]: {
          actions: ['updateActorResponses']
        },
        [questionEvents.valid]: { // if validation is successful in an actor - reset errors and prepare to go to the next question group
          actions: ['logEvent', 'resetErrors', 'enableAutoFocus', 'requestNextQuestionGroup'],
          cond: 'checkForErrors'
        },
        [questionEvents.invalid]: { // inversely if validation fails, errors are set via resetErrors
          target: 'editing.active.manualNav',
          actions: ['logEvent', 'resetErrors']
        },
        [questionEvents.next]: [
          {
            actions: ['logEvent', 'requestNextQuestionGroup'],
            cond: 'isBillboard'
          }, { // used to trigger validation explicitly
            actions: 'logEvent',
            target: '.validating'
          }],
        // Can either move to the next question or to the "done"
        // state when the question machine is at the end of the questions array
        // in the machine context.
        // This event is no longer called from the component, NEXT is instead called to
        // trigger validation from the actors and then fire this event upon receiving the VALID event
        [questionEvents.nextQuestionGroup]: [
          {
            target: 'changeConfigUrl',
            actions: [
              'evaluateBranching',
              'broadcastQuestionResponseEvent',
              'broadcastNextButtonClickEvent',
              'broadcastChangeConfigUrl'
            ],
            cond: 'isConfigChanging'
          },
          {
            target: 'finalizing',
            actions: ['broadcastQuestionResponseEvent', 'broadcastSubmitButtonClickEvent', 'accordionClose'],
            cond: 'isLastGroup'
          },
          {
            target: 'editing.active.hist',
            actions: [
              'evaluateBranching',
              'broadcastQuestionResponseEvent',
              'broadcastNextButtonClickEvent',
              'moveToNextQuestionGroup',
              'createActors',
              'accordionClose'
            ]
          }
        ],
        [questionEvents.finalizeComplete]: [
          {
            target: 'postFunnelExperience',
            cond: 'enablePostFunnelExperience'
          },
          {
            target: 'redirectionExit'
          }
        ],
        [questionEvents.finalizeError]: {
          target: 'errorFinalizing'
        },
        [questionEvents.prev]: {
          target: 'editing.active.hist',
          actions: ['broadcastBackButtonClickEvent', 'moveToPrevQuestionGroup', 'accordionClose'],
          cond: 'canUseBackNavigation'
        },
        [questionEvents.startAutoNav]: {
          target: 'editing.active.autoNav',
          actions: ['storeAutoNavTarget']
        },
        [questionEvents.authSuccess]: {
          actions: 'broadcastAuthSuccess'
        },
        [questionEvents.authFailure]: {
          actions: 'broadcastAuthFailure'
        }
      }
    },
    // When we reach this state we want to package all the question_key and value
    // properties and send them back as an event payload for the funnel machine
    // it is tagged as editing in order to display the preload overlay on top of the existing
    // question machine component
    changeConfigUrl: {
      type: 'final',
      tags: ['editing'],
      data: ctx => ({
        question_groups: ctx.question_groups,
        targetConfigUrl: ctx.question_group.winningBranch.target_config_url
      })
    },
    finalizing: {
      entry: ['saveResponses', 'logFinalize'],
      invoke: {
        id: 'awaitTransmissionsDone',
        src: 'awaitTransmissionsDone',
        onDone: [
          {
            target: 'postFunnelExperience',
            cond: 'enablePostFunnelExperience'
          },
          {
            target: 'redirectionExit'
          }
        ],
        onError: {
          target: 'errorFinalizing'
        }
      }
    },
    redirectionExit: {
    },
    postFunnelExperience: {
    },
    errorFinalizing: {
    }
  }
}

export const questionMachineOptions = {
  services: {
    awaitTransmissionsDone: ctx => awaitTransmissionsDone(ctx)
  },
  actions: {
    readyToParent: sendParent(funnelEvents.questionMachineReady),
    storeForRetargeting: (ctx, event) => {
      storeForRetargeting(event)
    },
    enableAutoFocus: assign({
      autofocus_enabled: true
    }),
    accordionClose: sendParent(funnelEvents.accordionClose),
    setInitialQuestionGroup: assign({
      question_group: ctx => ctx.question_groups[0]
    }),
    createActors: assign({
      // iterate through each question group and create an input machine for each question
      // these will fire events to the question machine
      question_group: (ctx, event) => prepareActors(ctx.question_group, ctx.autofocus_enabled, event)
    }),
    updateResponses: assign((ctx, event) => updateMachineContext(ctx, event,
      (event, q) => ({
        ...q,
        value: event.data.value
      }))),
    resetErrors: assign((ctx, event) => updateMachineContext(ctx, event,
      (event, q) => ({
        ...q,
        errors: event.data.errors
      }))),
    completeIntermission: assign(
      ctx => updateQuestionGroupMeta(ctx,
        (g) => ({
          ...g,
          meta_content: {
            ...g.meta_content,
            intermission: {
              ...g.meta_content.intermission,
              complete: true
            }
          }
        })
      )
    ),
    evaluateBranching: assign(ctx => ({
      question_group: evaluateBranching(ctx.question_groups, ctx.question_group)
    })),
    moveToNextQuestionGroup: assign(ctx => {
      const nextQuestionGroup = getNextQuestionGroup(ctx.question_groups, ctx.question_group)
      return {
        question_group: nextQuestionGroup
      }
    }),
    moveToPrevQuestionGroup: assign(ctx => ({
      question_group: ctx.question_groups.find(g => g.group_key === ctx.question_group.prevQuestionGroupKey)
    })),
    assignPopupURL: assign((ctx, event) => ({
      popupData: {
        popup_url: event.data?.popup_url,
        popup_url_target: event.data?.popup_url_target
      }
    })),
    broadcastChangeConfigUrl: ctx => {
      broadcast(broadcastEvents.changeConfig, {
        targetConfigUrl: ctx.question_group.winningBranch.target_config_url,
        group_key: ctx.question_group.group_key
      }, ctx)
    },
    broadcastQuestionResponseEvent: (ctx) => {
      ctx.question_group.questions?.forEach(q => {
        const data = {
          group_key: ctx.question_group.group_key,
          question_key: q.question_key,
          value: q.value,
          questionValue: q.questionValue,
          question_text: q.question_text,
          answer_type: q.answer_type,
          answer_choices: q.answer_choices?.map(ac => ({
            text: ac.description,
            value: ac.value,
            is_default: ac.default
          }))
        }
        broadcast(broadcastEvents.questionResponse, data, ctx)
        Logger.log(logLevels.INFO, 'Response to question', data, ctx)
      })
    },
    broadcastNextButtonClickEvent: (ctx) => {
      const nextQuestionGroup = getNextQuestionGroup(ctx.question_groups, ctx.question_group)
      const data = {
        from: ctx.question_group.group_key,
        to: nextQuestionGroup?.group_key,
        questions: ctx.question_group.questions?.map(q => ({
          question_key: q.question_key,
          value: q.value
        }))
      }
      broadcast(broadcastEvents.nextButtonClick, data, ctx)
      Logger.log(logLevels.TRACE, 'Next button click', data, ctx)
    },
    broadcastSubmitButtonClickEvent: (ctx) => {
      const data = {
        from: ctx.question_group.group_key,
        questions: ctx.question_group.questions?.map(q => ({
          question_key: q.question_key,
          value: q.value
        }))
      }
      broadcast(broadcastEvents.submitButtonClick, data, ctx)
      Logger.log(logLevels.TRACE, 'Submit button click', data, ctx)
    },
    broadcastBackButtonClickEvent: (ctx, event) => {
      const data = {
        ...event,
        from: ctx.question_group.group_key,
        to: ctx.question_group.prevQuestionGroupKey
      }
      broadcast(broadcastEvents.backButtonClick, data, ctx)
      Logger.log(logLevels.TRACE, 'Back button click', data, ctx)
    },
    broadcastAuthSuccess: (ctx, event) => {
      broadcast(broadcastEvents.authSuccess, event.data, ctx)
    },
    broadcastAuthFailure: (ctx, event) => {
      broadcast(broadcastEvents.authFailure, event.data, ctx)
    },
    // Using pure actions in order to send the same event to multiple actors
    // see: https://xstate.js.org/docs/guides/actions.html#pure-action for more details
    startValidation: actions.pure(ctx =>
      ctx.question_group.questions?.map(q => actions.send(inputEvents.validate, { to: q.ref }))
    ),
    // Update the answers of an actor(s) running within the question machine
    // currently only works for text field machines
    updateActorResponses: actions.pure((ctx, event) => {
      const { events: actorEvents } = event?.data
      const actorActions = []
      for (const actorEvent of actorEvents) {
        const questionGroup = ctx.question_groups.find(g => g.group_key === actorEvent.groupKey)
        if (questionGroup) {
          const question = questionGroup.questions?.find(q => q.question_key === actorEvent.id)
          if (question && question?.ref) {
            actorActions.push(actions.send({
              type: inputEvents.change,
              value: actorEvent.value
            }, { to: question.ref }))
          }
        }
      }
      return actorActions
    }),
    requestNextQuestionGroup: send(questionEvents.nextQuestionGroup),
    storeAutoNavTarget: assign({
      autoNavTargetGroup: (ctx, event) => event.targetGroup
    }),
    logEvent: (ctx, event) => {
      Logger.log(logLevels.TRACE, 'General QuestionMachine event', {
        event
      }, ctx)
    },
    saveResponses: assign({
      responses: (ctx, event) => ([
        ...ctx.responses, // any previous responses
        ...prepareResponses(ctx.question_groups)
      ])
    }),
    logFinalize: (ctx) => {
      const sessionId = window.com_smartasset_smartadvisor_funnel?.leaduser_session_id
      broadcast(broadcastEvents.funnelFinalize, {
        session_id: sessionId > 0 ? sessionId : -1,
        valid_lead: sessionId > 0
      }, ctx)
    }
  },
  guards: {
    isLastGroup: ctx => getCurrentQuestionGroupIndex(ctx.question_groups, ctx.question_group) === ctx.question_groups.length - 1,
    canUseBackNavigation: ctx => (ctx.question_group.prevQuestionGroupKey && !ctx.question_group.disableBackNavigation),
    canEnterIntermission: ctx => (ctx.question_group?.meta_content?.intermission && !ctx.question_group?.meta_content?.intermission?.complete),
    checkForErrors: ctx => {
      let errorFree = true
      ctx.question_group.questions?.forEach(q => {
        // we are checking each actor within the current question group for a valid state
        // using tags instead of actual state names for simplicity
        if (!q.ref.state.hasTag('valid')) {
          errorFree = false
        }
      })
      return errorFree
    },
    isBillboard: ctx => !!ctx.question_group.billboard,
    isTimedBillboard: ctx => !!ctx.question_group?.billboard?.delay,
    isLoadingPanel: ctx => !!ctx.question_group?.loadingPanel,
    isConfigChanging: ctx => !!ctx.question_group?.winningBranch?.target_config_url,
    isSingleClick: ctx => !!ctx.question_group?.meta_content?.is_single_click,
    enablePostFunnelExperience: (ctx) => ctx.postFunnelExperienceConfig?.iframe
  },
  delays: {
    [questionDelays.intermissionDelay]: ctx => ctx.question_group?.meta_content?.intermission?.delay_time || 500,
    [questionDelays.billboardDelay]: ctx => ctx.question_group?.billboard?.delay,
    [questionDelays.loadingPanelDelay]: ctx => ctx.question_group?.loadingPanel?.delay
  }
}

export const questionMachine = createMachine(questionMachineDeclaration, questionMachineOptions)
